import { useAgent } from "@copilotkit/react-core/v2";
import { AppIcon } from "@memorang/ui/components/AppIcon";
import { ChatAppbar as Appbar } from "@memorang/ui/components/RedeemCopilot/Chat/Appbar";
import type { IconType } from "@memorang/ui/icon";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import useBreakpoints from "@memorang/ui/useBreakpoints";
import type React from "react";
import { memo, useCallback, useEffect } from "react";
import { type CursorValue, Pressable, View } from "react-native";
import { FAB } from "react-native-paper";
import { events, useRedeemCopilotAnalytics } from "../../analytics";
import { APPS_REDEEM_AGENT_NAME } from "../../constants";
import { useRedeemCopilot } from "../../context";

type Props = {
	title?: string;
	open?: boolean;
	greetingMessage?: string;
	children?: React.ReactNode;
};

const offsetX = 16;
const offsetY = 16;

const ChatFAB = memo(
	({
		isOpen,
		onPress,
		isMobile,
	}: {
		isOpen: boolean;
		onPress: () => void;
		isMobile: boolean;
	}) => {
		const theme = useAppTheme();
		const icon = isOpen ? "chevron-down" : "chat";

		return (
			<FAB
				mode="elevated"
				icon={(props) => <AppIcon name={icon as IconType} {...props} />}
				onPress={onPress}
				color={theme.colors.onPrimary}
				style={{
					position: "absolute",
					bottom: offsetY,
					right: offsetX,
					zIndex: 2,
					backgroundColor: theme.colors.primary,
					display: isMobile && isOpen ? "none" : "flex",
				}}
			/>
		);
	},
);

export const Popup = ({ title, open, children, greetingMessage }: Props) => {
	const theme = useAppTheme();
	const { xsDown } = useBreakpoints();

	const {
		chatState: {
			chatOpen: openState,
			isFirstTimeChatOpened,
			assistantTitle,
			authReady,
		},
		setPartialChatState,
	} = useRedeemCopilot();

	const { agent } = useAgent({ agentId: APPS_REDEEM_AGENT_NAME });

	const chatOpen = open ?? openState;
	const chatTitle = assistantTitle || title;

	const { trackRedeemCopilotEvent } = useRedeemCopilotAnalytics();

	const handleToggleChat = useCallback(() => {
		setPartialChatState({ chatOpen: !chatOpen });
	}, [chatOpen, setPartialChatState]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Only track when chat is opened or closed
	useEffect(() => {
		trackRedeemCopilotEvent(chatOpen ? events.chatOpened : events.chatClosed, {
			type: "success",
			message: chatOpen ? "Chat opened" : "Chat closed",
			isFirstTimeChatOpened,
		});
	}, [chatOpen]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: setPartialChatState is used in the useEffect
	useEffect(() => {
		if (chatOpen && isFirstTimeChatOpened && authReady && greetingMessage) {
			agent.setMessages([
				{
					id: globalThis.crypto.randomUUID(),
					role: "assistant",
					content: greetingMessage,
				},
			]);
			setPartialChatState({ isFirstTimeChatOpened: false });
		}
	}, [chatOpen, greetingMessage, isFirstTimeChatOpened, authReady, agent]);

	return (
		<>
			<ChatFAB isOpen={chatOpen} onPress={handleToggleChat} isMobile={xsDown} />
			<View
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					zIndex: 1,
					display: chatOpen ? "flex" : "none",
				}}
			>
				<Pressable
					style={{ flex: 1 }}
					onPress={() => setPartialChatState({ chatOpen: false })}
				>
					<View
						style={{
							position: "absolute",
							top: xsDown ? 0 : undefined,
							left: xsDown ? 0 : undefined,
							bottom: xsDown ? 0 : offsetY + 72,
							right: xsDown ? 0 : offsetX,
							width: xsDown ? "100%" : 400,
							height: xsDown ? "100%" : "85%",
							backgroundColor: theme.colors.surface,
							borderRadius: xsDown ? 0 : theme.roundness,
							shadowColor: theme.colors.elevation[0],
							shadowOpacity: 0.1,
							shadowRadius: 10,
							elevation: 5,
							zIndex: 2,
							overflow: "hidden",
							display: "flex",
						}}
					>
						<Pressable
							style={{ flex: 1, display: "flex" }}
							onPress={(e) => e.stopPropagation()}
						>
							<Appbar
								title={chatTitle}
								showClose={xsDown}
								handleClose={() => setPartialChatState({ chatOpen: false })}
							/>
							<View
								style={{
									flex: 1,
									display: "flex",
									minHeight: 0,
									padding: 8,
									paddingBottom: 32,
									gap: 16,
									cursor: "default" as unknown as CursorValue,
								}}
							>
								{children}
							</View>
						</Pressable>
					</View>
				</Pressable>
			</View>
		</>
	);
};
